import React, { Component } from 'react';
import { ClipboardCheck, TriangleAlert } from 'lucide-react';
import Inherit from '../../core/inherit';
import withNavigate from '../../core/withNavigate';
import StoreCheckController from './storecheck.controller';
import { User } from '../../data';
import { canSeePrices } from '../../core/access';

import { AnalyticsInfo, Layout, CardSkeleton, AnalyticsSkeleton, MissingProducts, ShareOfShelf, PageHeader, EmptyState } from '../common';
import { PriceTable, StoreCheckTitle, PromotorImages, AdditionalInfo, PromotionalActivity, AsesorImages } from './elements';

/**
 * Store check detail. Base page: a company replaces one section by subclassing this class and
 * overriding its seam (`renderInventory`, `renderProducts`, …), calling `super` for the rest,
 * then registering the subclass in `companies/<id>/index.js` (`pages.StoreCheck`).
 */
export class ShelfStoreCheckProfile extends Inherit(StoreCheckController, Component) {
    state = {
        storeCheck: {},
        loading: true,
        elementsLoading: true,
        prices: [],
        date: '',
        store: {}
    };

    /** Page header: what this page is. */
    renderHeader() {
        return <PageHeader icon={<ClipboardCheck />} title="Detalle de Auditoría" description="Información completa de la visita" />;
    }

    /** The store check could not be loaded. */
    renderNotFound() {
        return (
            <Layout current="store_check">
                {this.renderHeader()}
                <div className="shelf-card max-w-md mx-auto mt-6">
                    <EmptyState
                        tone="danger"
                        icon={<TriangleAlert />}
                        title="Auditoría no encontrada"
                        description="La auditoría solicitada no pudo ser cargada."
                    />
                </div>
            </Layout>
        );
    }

    /** Skeleton while the store check is loading. */
    renderLoading() {
        return (
            <Layout current="store_check">
                <div className="flex flex-col gap-y-6">
                    {/* Page header skeleton */}
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-surface-100 animate-pulse" />
                        <div>
                            <div className="w-56 h-6 rounded-md bg-surface-100 animate-pulse mb-2" />
                            <div className="w-40 h-4 rounded-md bg-surface-100 animate-pulse" />
                        </div>
                    </div>
                    <CardSkeleton rows={2} />
                    <AnalyticsSkeleton cards={4} />
                    <CardSkeleton rows={8} />
                </div>
            </Layout>
        );
    }

    /** Summary: what, who, when. */
    renderSummary(storeCheck) {
        const { date, store } = this.state;
        return <StoreCheckTitle storeCheck={storeCheck} date={date} store={store} />;
    }

    /** Visit KPIs. */
    renderAnalytics(storeCheck) {
        return <AnalyticsInfo storeChecks={[storeCheck]} specific />;
    }

    /** Prices captured on the visit (asesor checks, users who may see prices). */
    renderPriceTable(storeCheck, isPromotor) {
        if (isPromotor || !canSeePrices(User.getInstance()?.userType)) return null;
        const { prices, store } = this.state;
        return <PriceTable prices={prices} channel={this.getChannel(store)} />;
    }

    /** Before / after photos: the promotor's work or the asesor's pictures. */
    renderImages(storeCheck, isPromotor) {
        return isPromotor ? <PromotorImages storeCheck={storeCheck} /> : <AsesorImages storeCheck={storeCheck} />;
    }

    renderPromotionalActivity(storeCheck) {
        return <PromotionalActivity storeCheck={storeCheck} />;
    }

    renderAdditionalInfo(storeCheck) {
        return <AdditionalInfo storeCheck={storeCheck} />;
    }

    /** The warehouse inventory captured on the visit. The base page does not show it. */
    renderInventory(storeCheck) {
        return null;
    }

    /** Shelf products: those missing from the shelf. */
    renderProducts(storeCheck) {
        return <MissingProducts products={storeCheck?.products || []} />;
    }

    renderShareOfShelf(storeCheck) {
        return storeCheck ? <ShareOfShelf storeChecks={[storeCheck]} /> : null;
    }

    /** Detail sections, in order. */
    renderSections(storeCheck) {
        const isPromotor = storeCheck?.user?.userType === 'promotor';
        return (
            <div className="flex flex-col gap-y-4">
                {this.renderPriceTable(storeCheck, isPromotor)}
                {this.renderImages(storeCheck, isPromotor)}
                {this.renderPromotionalActivity(storeCheck)}
                {this.renderAdditionalInfo(storeCheck)}
                {this.renderInventory(storeCheck)}
                {this.renderProducts(storeCheck)}
                {this.renderShareOfShelf(storeCheck)}
            </div>
        );
    }

    /** Everything after the loading gate. */
    renderContent(storeCheck) {
        return (
            <Layout current="store_check">
                {this.renderHeader()}

                <div className="flex flex-col gap-y-6">
                    {this.renderSummary(storeCheck)}
                    {this.renderAnalytics(storeCheck)}
                    {this.renderSections(storeCheck)}
                </div>
            </Layout>
        );
    }

    render() {
        const { storeCheck, loading } = this.state;

        // Safety check to prevent crashes if storeCheck is null/undefined
        if (!loading && !storeCheck) return this.renderNotFound();
        if (loading) return this.renderLoading();
        return this.renderContent(storeCheck);
    }
}

export default withNavigate(ShelfStoreCheckProfile);
